import clone from 'lodash/clone'
import cloneDeep from 'lodash/cloneDeep'
import { DateTime } from 'luxon'
import type {
  BpCoupon,
  BpLocation,
  CheckOrderingIsEnabledResponse,
  DeliverabilityResponse,
  RootData, StoreResponse, TwAlertOptions,
} from '~/definitions/AllDefinitions'
import { isTodayOpen } from '~/classes/HourUtils'
import { useOrderStore } from '~/store/OrderStore'
import { DELIVERY_METHODS } from '~/definitions/enums'
import type { MainStoreInterface } from '~/definitions/StateDefinitions'

export const useMainStore = defineStore('main-store', {
  state: (): MainStoreInterface => ({
    deliveryMethods: [],
    id: 0,
    rootData: {
      stripeAccount: '',
      id: 0,
      archived: false,
      campaignId: 0,
      colorScheme: {
        accentColor: '#1155a4',
        accentContrastColor: '#FFFFFF',
        accentIsLightColor: false,
        navColor: '#FFFFFF',
        navContrastColor: '#1e293b',
        navIsLightColor: true,
      },
      currency: 'USD',
      currencyInfo: {
        code: 'USD',
        createdAt: '',
        updatedAt: '',
        decimalPoint: '.',
        enabled: true,
        format: '${price}',
        icon: 'currency-usd',
        isDefault: true,
        name: 'US Dollar',
        symbol: '$',
        thousandPoint: ',',
      },
      domainName: '',
      hasBilling: false,
      language: 'en-US',
      locations: [],
      logo: {
        type: 'square',
      },
      name: '',
      networkId: 0,
      qrCode: null,
      seoMeta: {},
      timezone: 'America/Los_Angeles',
      uuid: '',
      paused: false,
    },
    currentLocation: null,
    shopCategories: [],
    nextUpdate: null,
    structuredData: {},
  }),
  getters: {
    accountName: state => state.rootData.name,
    accountId: state => state.rootData.id,
    currentDeliveryMethods: state => state.deliveryMethods,
    currencyCode: state => state.rootData && state.rootData.currencyInfo &&
      state.rootData.currencyInfo.code,
    currentLocations: state => (state.rootData.locations as BpLocation[]),
    hasOrderingCapability: state => state.rootData.hasBilling && state.currentLocation &&
      state.currentLocation.id && state.currentLocation.enableOrdering,
    isLocationOpen: (state: MainStoreInterface) => {
      return !!(state.currentLocation?.id &&
        isTodayOpen(state.rootData.timezone, state.currentLocation.businessHours))
    },
    isOrderingEnabled: (state: MainStoreInterface) => {
      const isOpen = isTodayOpen(state.rootData.timezone, state.currentLocation?.orderingHours)
      return !!(
        isOpen &&
        state.rootData?.hasBilling &&
        state.currentLocation?.id &&
        state.currentLocation?.enableOrdering
      )
    },
    locationId: state => state.currentLocation && state.currentLocation.id,
    navIsLightColor: state => state.rootData && state.rootData.colorScheme &&
      state.rootData.colorScheme.navContrastColor && state.rootData.colorScheme.navContrastColor === '#FFFFFF',
    timeZone: state => state.rootData && state.rootData.timezone,
  },
  actions: {
    async checkLocationDeliverability(locationId: number, latitude: number, longitude: number) {
      if (!(this.accountId && locationId && latitude && longitude)) {
        return false
      }
      try {
        const data = await apiFetch(`/api/${this.accountId}/locations/${locationId}/proximity`, {
          query: {
            latitude,
            longitude,
          },
        })
        if (data) {
          const ret = data as DeliverabilityResponse
          return ret.deliverable
        }
        return false
      } catch {
        return false
      }
    },
    async checkIsOrderingEnabled() {
      const defaultResponse = {
        data: false,
        success: false,
        error: {
          type: 'error',
          headline: 'LOCATION_CLOSED',
          items: [],
          display: true,
        } as TwAlertOptions,
      } as StoreResponse
      if (!(this.accountId && this.locationId)) {
        return defaultResponse
      }
      try {
        const data = await apiFetch(`/api/${this.accountId}/locations/${this.locationId}/checks`)
        if (data) {
          const ret = data as CheckOrderingIsEnabledResponse
          this.rootData = cloneDeep(ret.rootData)
          this.currentLocation = cloneDeep(ret.location)
          return ret.isOrderingEnabled
            ? { error: null, success: true, data: true }
            : defaultResponse
        }
        this.clearRootData()
        this.currentLocation = null
        return defaultResponse
      } catch {
        return defaultResponse
      }
    },
    clearRootData() {
      this.rootData.id = 0
      this.rootData.name = ''
      this.rootData.domainName = ''
      this.rootData.hasBilling = false
      this.deliveryMethods.length = 0
    },
    async getLocation(locationId: number) {
      if (!this.accountId) {
        return
      }
      const locationCookie = useCookie<number | null>('bp_location_id')
      try {
        const data = await apiFetch(`/api/${this.accountId}/locations/${locationId}`)
        if (data) {
          this.currentLocation = data as BpLocation
        } else {
          this.currentLocation = null
        }
      } catch {
        this.currentLocation = null
      }
      locationCookie.value = this.currentLocation?.id ?? null
    },
    async getRootData(domain: string) {
      if (!domain) {
        this.clearRootData()
        return
      }
      try {
        const data = await apiFetch<RootData>(`/api/domains/${domain}`)
        if (data) {
          this.rootData = data
          this.nextUpdate = DateTime.now().plus({ minutes: 15 }).toMillis()
          if (this.rootData.locations && this.rootData.locations.length === 1) {
            this.currentLocation = cloneDeep(this.rootData.locations[0])
          }
        } else {
          this.rootData.id = 0
          this.deliveryMethods.length = 0
        }
      } catch {
        this.rootData.id = 0
        this.deliveryMethods.length = 0
      }
      this.deliveryMethods.length = 0
      if (this.currentLocation?.enablePickup) {
        this.deliveryMethods.push({
          label: 'PICKUP',
          value: DELIVERY_METHODS.PICKUP,
        })
      }
      if (this.currentLocation?.enableDelivery) {
        this.deliveryMethods.push({
          label: 'DELIVERY',
          value: DELIVERY_METHODS.DELIVERY,
        })
      }
      if (this.deliveryMethods.length) {
        const orderStore = useOrderStore()
        const validCurrentMethods = this.deliveryMethods.filter((deliveryMethod) => {
          return deliveryMethod.value === orderStore.order.deliveryMethod
        })
        if (!orderStore.order.deliveryMethod || !validCurrentMethods.length) {
          orderStore.order.deliveryMethod = clone(this.deliveryMethods[0].value)
        }
      }
    },
    async getStructuredData() {
      try {
        const data = await apiFetch(`/api/${this.accountId}/structured-data`)
        if (data) {
          this.structuredData = data
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        this.structuredData = {}
      }
    },
    async validateCoupon(code: string, cartId: string): Promise<BpCoupon | StoreResponse> {
      try {
        const url = `/api/${this.accountId}/locations/${this.locationId}/coupons/validate/${code}`
        const data = await apiFetch(url, {
          query: {
            cartId,
          },
        })
        return { ...data as BpCoupon }
      } catch (err: any) {
        const formatError = useFormatError()
        return { success: false, data: null, error: formatError(err) }
      }
    },
  },
})
